/* eslint-disable max-lines */
import React, { Fragment, useEffect, useMemo } from 'react';
import { createStructuredSelector } from 'reselect';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import Box from '@quintoandar/cozy-core/Box';
import AppBar from 'components/AppBar';
import Snackbar from '@quintoandar/cozy-core/Snackbar';
import AddAPhoto from '@quintoandar/cozy-icons/AddAPhoto';
import { LoadingPage } from 'components/LoadingPage';
import { getUser } from 'utils/user';
import ROUTES from 'routes/constants';
import { PHOTO_SESSION_STATUS } from 'constants/photoSession';
import ErrorBoundary from 'components/ErrorBoundary';
import { STATUS } from 'containers/PropertyAssessment/constants';
import { logUncaught } from 'utils/log';
import useCanAssessProperty from 'data/react-query/queries/useCanAssessProperty';
import useAssessment from 'containers/PropertyAssessment/hooks/useAssessment';
import Onboarding from 'components/Onboarding';

import { selectPhotoSessionDetail } from '../PhotoSessionDetail/selectors';

import {
  startPhotoSessionAction,
  checklistPageViewedAnalytics,
  fetchChecklistAction,
  fetchChecklistFromStorageAction,
  finishChecklistErrorUpdate,
  updateHouseAddressAction,
  updateShouldOpenDialogAction,
  openFinishSessionDialog,
  updateIsChecklistValidAction,
} from './actions';

import { makeSelectFinishSessionError, selectChecklist } from './selectors';
import messages, { mountFinishSessionError } from './messages';
import { SECTIONS_TYPE } from './constants';
import { getProgress, isSectionFinished } from './progress';
import FinishSessionDialog from './FinishSessionDialog';
import OverviewForm from './OverviewForm';
import {
  changeEnabledButtonAction,
  changeBottomButtonPayloadAction,
} from '../AnnouncementSteps/actions';
import { isChecklistStorageReady } from './utils/checklistStorage';
import { buildHouseAddressParamsFromPhotoSession } from './utils/houseAddressMerge';

const prefillHouseAddressFromPhotoSession = ({
  photoSessionDetail,
  updateHouseAddress,
  houseItems,
  onlyFillMissing,
}) => {
  updateHouseAddress(
    buildHouseAddressParamsFromPhotoSession(photoSessionDetail, {
      existingItems: houseItems,
      onlyFillMissing,
    }),
  );
};

const ContentComponent = ({
  user,
  checklist,
  onFinishSessionClick,
  progress,
  photoSessionId,
  shouldNotShowComponents,
  onCloseSnackbar,
  shouldOpenDialogWithoutError,
  closeDialog,
  isSectionsFinished,
  additionalProps,
  finishSessionError,
  intl,
  houseId,
  house,
}) => {
  const dataToOnboardingToChecklist = useMemo(
    () => [
      {
        title: messages.checklistOnboardingStep1Title,
        description: messages.checklistOnboardingStep1Description,
        tag: {
          kind: 'default',
          label: intl.formatMessage(messages.checklistOnboardingDefaultLabel),
          icon: <AddAPhoto />,
        },
      },
      {
        title: messages.checklistOnboardingStep2Title,
        description: messages.checklistOnboardingStep2Description,
        tag: {
          kind: 'default',
          label: intl.formatMessage(messages.checklistOnboardingDefaultLabel),
          icon: <AddAPhoto />,
        },
      },
      {
        title: messages.checklistOnboardingStep3Title,
        description: messages.checklistOnboardingStep3Description,
        tag: {
          kind: 'default',
          label: intl.formatMessage(messages.checklistOnboardingDefaultLabel),
          icon: <AddAPhoto />,
        },
      },
    ],
    [intl],
  );

  return (
    <Fragment>
      <OverviewForm
        house={house}
        houseId={houseId}
        userId={user.user_id}
        isValid={checklist.isChecklistValid}
        onFinishSessionClick={onFinishSessionClick}
        isSectionsFinished={isSectionsFinished}
        fixedSection={checklist.fixed}
        progress={progress}
        photoSessionId={photoSessionId}
        shouldNotShowComponents={shouldNotShowComponents}
        checklist={checklist}
      />
      <Onboarding
        dataToOnboarding={dataToOnboardingToChecklist}
        onboardingReference="checklist_onboarding"
        btnTextFinish={intl.formatMessage(
          messages.checklistOnboardingBtnTextFinish,
        )}
      />

      <ErrorBoundary errorComponent={null} logError={logUncaught}>
        <Snackbar
          open={checklist.hasFinishSessionError}
          onClose={onCloseSnackbar}
          color="error"
          message={intl.formatMessage(
            messages.errorMessageWithDetails,
            mountFinishSessionError(finishSessionError).values,
          )}
        />
      </ErrorBoundary>
      <FinishSessionDialog
        isSessionFinished={checklist.isSessionFinished}
        isDialogLoading={checklist.isDialogLoading}
        isDialogOpen={shouldOpenDialogWithoutError}
        onCloseDialog={closeDialog}
        photoSessionId={photoSessionId}
        checklist={checklist}
        photoSessionInfo={additionalProps}
        newChecklist={shouldNotShowComponents}
      />
    </Fragment>
  );
};

ContentComponent.propTypes = {
  houseId: PropTypes.number,
  house: PropTypes.object,
  user: PropTypes.object,
  checklist: PropTypes.object,
  onFinishSessionClick: PropTypes.func,
  progress: PropTypes.number,
  photoSessionId: PropTypes.string,
  shouldNotShowComponents: PropTypes.bool,
  onCloseSnackbar: PropTypes.func,
  shouldOpenDialogWithoutError: PropTypes.bool,
  closeDialog: PropTypes.func,
  isSectionsFinished: PropTypes.func,
  additionalProps: PropTypes.func,
  finishSessionError: PropTypes.object,
  intl: PropTypes.object,
};

export const Checklist = ({
  fetchChecklist,
  fetchChecklistFromStorage,
  photoSessionDetail,
  startPhotoSession,
  trackPageView,
  goBack,
  updateFinishChecklistError,
  checklist,
  updateHouseAddress,
  closeDialog,
  openDialog,
  updateChecklistIsValid,
  changeEnabledButton,
  shouldNotShowComponents,
  changeBottomButtonPayload,
  additionalProps,
  finishSessionError,
  params,
  intl,
}) => {
  const photoSessionId = additionalProps.photoSessionId || params.id;
  const houseId = Number(additionalProps.houseId || params.house_id);

  useEffect(() => {
    if (!photoSessionDetail.id) {
      return;
    }

    trackPageView({
      photoSessionId,
      houseId: photoSessionDetail.house.id,
      photographerId: getUser().user_id,
    });
  }, [photoSessionDetail.id]);

  useEffect(() => {
    if (!photoSessionDetail.id) {
      return;
    }

    prefillHouseAddressFromPhotoSession({
      photoSessionDetail,
      updateHouseAddress,
      houseItems: checklist.fixed?.house?.items,
      onlyFillMissing: checklist.hasFetchedChecklistItemsFromStorage,
    });
  }, [photoSessionDetail.id, checklist.hasFetchedChecklistItemsFromStorage]);

  /* istanbul ignore next */
  useEffect(() => {
    if (!checklist.hasFetchedChecklistItems) {
      fetchChecklist();
    }
    fetchChecklistFromStorage({ userId: getUser().user_id, photoSessionId });
  }, []);

  if (checklist.showError || photoSessionDetail.showError) {
    goBack(photoSessionId);
  }

  const statusToUpdate = [
    PHOTO_SESSION_STATUS.ACEITO,
    PHOTO_SESSION_STATUS.DESIGNADO_FOTOGRAFO,
    PHOTO_SESSION_STATUS.AGENDADO,
  ];

  const startSessionIfNeeded =
    ({ photoSessionStatus }) =>
      () =>
        statusToUpdate.includes(photoSessionStatus) &&
      startPhotoSession(photoSessionId);

  useMemo(
    startSessionIfNeeded({ photoSessionStatus: photoSessionDetail.status }),
    [photoSessionDetail.status],
  );

  const {
    data: canAssessProperty = false,
    isLoading: isLoadingCanAssessProperty,
  } = useCanAssessProperty(photoSessionId);
  const { data: assessment, isLoading: isLoadingAssessment } =
    useAssessment(photoSessionId);

  const progress = getProgress({
    checklist,
    assessment,
  });

  const hasFinishedAssessment = canAssessProperty
    ? assessment?.status === STATUS.COMPLETED
    : true;

  const shouldFinish =
    progress.total !== 0 &&
    progress.total === progress.current &&
    checklist.fixed.house.items.tipo &&
    checklist.fixed.house.items.tipo.value !== undefined &&
    hasFinishedAssessment;

  const onFinishSessionClick = () => {
    if (shouldFinish) {
      return openDialog();
    }

    return updateChecklistIsValid(false);
  };

  /* istanbul ignore next */
  const onCloseSnackbar = () => {
    closeDialog();
    updateFinishChecklistError({ hasFinishSessionError: false });
  };

  useEffect(() => {
    changeEnabledButton(shouldFinish);
    changeBottomButtonPayload({ checklist, assessment });
  }, [shouldFinish]);

  const isSectionsFinished = {
    house: isSectionFinished({
      checklist,
      sectionTypeName: SECTIONS_TYPE.HOUSE,
    }),
    externalArea: isSectionFinished({
      checklist,
      sectionTypeName: SECTIONS_TYPE.EXTERNAL_AREA,
    }),
    generalConditions: canAssessProperty
      ? assessment?.status === STATUS.COMPLETED
      : isSectionFinished({
        checklist,
        sectionTypeName: SECTIONS_TYPE.GENERAL_CONDITIONS,
      }),
  };

  const shouldOpenDialogWithoutError =
    !checklist.hasFinishSessionError && checklist.shouldOpenDialog;

  const shouldRenderChecklist =
    isChecklistStorageReady(checklist) &&
    !isLoadingAssessment &&
    !isLoadingCanAssessProperty;

  const headerGoBack = () => goBack(photoSessionId);

  return (
    <Fragment>
      {!shouldNotShowComponents && (
        <Box mb={2}>
          <AppBar onBack={headerGoBack} title={messages.title} />
        </Box>
      )}
      {shouldRenderChecklist ? (
        <ContentComponent
          user={getUser()}
          onFinishSessionClick={onFinishSessionClick}
          progress={progress}
          checklist={checklist}
          photoSessionId={photoSessionId}
          house={photoSessionDetail.house}
          houseId={houseId}
          shouldNotShowComponents={shouldNotShowComponents}
          onCloseSnackbar={onCloseSnackbar}
          shouldOpenDialogWithoutError={shouldOpenDialogWithoutError}
          closeDialog={closeDialog}
          isSectionsFinished={isSectionsFinished}
          additionalProps={additionalProps}
          finishSessionError={finishSessionError}
          intl={intl}
        />
      ) : (
        <LoadingPage />
      )}
    </Fragment>
  );
};

export const mapStateToProps = createStructuredSelector({
  photoSessionDetail: selectPhotoSessionDetail,
  checklist: selectChecklist,
  finishSessionError: makeSelectFinishSessionError(),
});

export const mapDispatchToProps = (dispatch) => ({
  fetchChecklist: () => dispatch(fetchChecklistAction()),
  fetchChecklistFromStorage: ({ userId, photoSessionId }) =>
    dispatch(fetchChecklistFromStorageAction({ userId, photoSessionId })),
  startPhotoSession: (photoSessionId) =>
    dispatch(startPhotoSessionAction(photoSessionId)),
  goBack: (photoSessionId) =>
    dispatch(push(`${ROUTES.PHOTO_SESSION_DETAIL}${photoSessionId}`)),
  updateFinishChecklistError: ({ hasFinishSessionError }) => {
    dispatch(finishChecklistErrorUpdate({ hasFinishSessionError }));
  },
  trackPageView: ({ photoSessionId, houseId, photographerId }) =>
    dispatch(
      checklistPageViewedAnalytics({
        photoSessionId,
        houseId,
        photographerId,
      }),
    ),
  updateHouseAddress: ({ floor, complement, number, street, neighborhood }) =>
    dispatch(
      updateHouseAddressAction({
        floor,
        complement,
        number,
        street,
        neighborhood,
      }),
    ),
  closeDialog: () => dispatch(updateShouldOpenDialogAction(false)),
  openDialog: () => dispatch(openFinishSessionDialog()),
  updateChecklistIsValid: (isValid) =>
    dispatch(updateIsChecklistValidAction(isValid)),
  changeEnabledButton: (enabledButton) =>
    dispatch(changeEnabledButtonAction(enabledButton)),
  changeBottomButtonPayload: (payload) =>
    dispatch(changeBottomButtonPayloadAction(payload)),
});

Checklist.propTypes = {
  updateHouseAddress: PropTypes.func.isRequired,
  fetchChecklist: PropTypes.func.isRequired,
  fetchChecklistFromStorage: PropTypes.func.isRequired,
  startPhotoSession: PropTypes.func.isRequired,
  photoSessionDetail: PropTypes.shape({
    status: PropTypes.string,
    id: PropTypes.number,
    showError: PropTypes.bool,
    house: PropTypes.shape({
      id: PropTypes.number,
    }).isRequired,
  }),
  trackPageView: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  checklist: PropTypes.shape({
    showError: PropTypes.bool,
    progress: PropTypes.number,
  }),
  updateFinishChecklistError: PropTypes.func.isRequired,
  additionalProps: PropTypes.shape({
    photoSessionId: PropTypes.number,
    houseId: PropTypes.number,
    houseType: PropTypes.string,
  }),
  closeDialog: PropTypes.func,
  openDialog: PropTypes.func,
  updateChecklistIsValid: PropTypes.func,
  changeEnabledButton: PropTypes.func,
  shouldNotShowComponents: PropTypes.bool,
  params: PropTypes.object,
  changeBottomButtonPayload: PropTypes.func,
  finishSessionError: PropTypes.object,
  intl: PropTypes.object,
};

export default compose(
  injectIntl,
  connect(mapStateToProps, mapDispatchToProps),
)(Checklist);
